import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { isPublicRouteEdition } from '../../constants/psaRouteEdition';
import {
    getPublicEditionDeferredRouteMode,
    hasDeferredModuleAccess,
} from '../../utils/deferredRouteAccess';
import { DeliveryCockpitEditionBlocked } from './DeliveryCockpitEditionBlocked';
import AppRouteNotFound from './AppRouteNotFound';

/**
 * Gates deferred enterprise routes.
 * Public edition: wedge personas see edition guard; admin/anon get generic 404.
 * Develop edition: admin + executive tier; wedge personas see edition message.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Protected route content.
 * @param {string} [props.featureName] - User-facing feature label (develop builds only).
 * @param {string[]} [props.additionalAllowedRoles] - Roles let through on develop builds
 *   for this specific route only, on top of the shared deferred-module policy. Use this
 *   instead of editing `DELIVERY_COCKPIT_EDITION_LIMITED_ROLES`, which is shared across
 *   every deferred route (CRM, analytics, Gantt, etc.) and would over-grant access.
 */
const DeliveryCockpitEditionGuard = ({
    children,
    featureName = 'This capability',
    additionalAllowedRoles = [],
}) => {
    const { user, loading } = useAuth();

    if (loading) {
        return null;
    }

    if (isPublicRouteEdition()) {
        if (getPublicEditionDeferredRouteMode(user) === 'edition-blocked') {
            return <DeliveryCockpitEditionBlocked featureName={featureName} />;
        }
        return <AppRouteNotFound />;
    }

    const hasRouteSpecificAccess = Boolean(user?.role) && additionalAllowedRoles.includes(user.role);

    if (hasDeferredModuleAccess(user) || hasRouteSpecificAccess) {
        return children;
    }

    return <DeliveryCockpitEditionBlocked featureName={featureName} />;
};

export default DeliveryCockpitEditionGuard;
